import React, { useEffect, useState } from "react";

/**
 * ReportGroupsEditor (enhanced)
 *
 * - Manage groups (list, add, edit, delete, toggle active)
 * - Assign/unassign members to groups via checkboxes
 *
 * Backend endpoints expected:
 * GET    /report_groups
 * POST   /report_groups           { name, is_active }
 * PUT    /report_groups/:id       { name, is_active }
 * DELETE /report_groups/:id
 *
 * GET    /report_members
 *
 * GET    /report_groups_members
 * POST   /report_groups_members   { group_id, member_id }
 * DELETE /report_groups_members/:groupId/:memberId
 */

export default function ReportGroupsEditor() {
  const [groups, setGroups] = useState([]);
  const [members, setMembers] = useState([]);
  const [groupMembersMap, setGroupMembersMap] = useState({}); // { groupId: Set(memberId) }
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Edit state
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", is_active: 0 });

  // New group form
  const [newGroup, setNewGroup] = useState({ name: "", is_active: 1 });

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    setError(null);
    try {
      const [groupsRes, membersRes, gmRes] = await Promise.all([
        fetch(process.env.REACT_APP_BACKEND_URL+"/report_groups"),
        fetch(process.env.REACT_APP_BACKEND_URL+"/report_members"),
        fetch(process.env.REACT_APP_BACKEND_URL+"/report_groups_members"),
      ]);

      if (!groupsRes.ok || !membersRes.ok || !gmRes.ok) {
        throw new Error("Failed to fetch resources");
      }

      const groupsData = await groupsRes.json();
      const membersData = await membersRes.json();
      const gmData = await gmRes.json();

      setGroups(Array.isArray(groupsData) ? groupsData : []);
      setMembers(Array.isArray(membersData) ? membersData : []);

      // build map { groupId: Set(memberId) }
      const gmMap = {};
      (gmData || []).forEach((g) => {
        if (!gmMap[g.group_id]) gmMap[g.group_id] = new Set();
        gmMap[g.group_id].add(g.member_id);
      });
      setGroupMembersMap(gmMap);
    } catch (err) {
      console.error("fetchAll error:", err);
      setError("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  // --- Group CRUD (same as before) ---

  const startEdit = (group) => {
    setEditingId(group.id);
    setEditForm({ name: group.name || "", is_active: group.is_active ? 1 : 0 });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm({ name: "", is_active: 0 });
  };

  const saveEdit = async (id) => {
    if (!editForm.name.trim()) {
      alert("Group name cannot be empty.");
      return;
    }
    const payload = { name: editForm.name.trim(), is_active: Number(editForm.is_active) ? 1 : 0 };
    try {
      const res = await fetch(process.env.REACT_APP_BACKEND_URL+`/report_groups/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      await fetchAll();
      cancelEdit();
    } catch (err) {
      console.error("Failed to save group:", err);
      alert("Failed to save group. See console for details.");
    }
  };

  const addGroup = async () => {
    if (!newGroup.name.trim()) {
      alert("Please enter a group name.");
      return;
    }
    const payload = { name: newGroup.name.trim(), is_active: Number(newGroup.is_active) ? 1 : 0 };
    try {
      const res = await fetch(process.env.REACT_APP_BACKEND_URL+"/report_groups", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setNewGroup({ name: "", is_active: 1 });
      await fetchAll();
    } catch (err) {
      console.error("Failed to add group:", err);
      alert("Failed to add group.");
    }
  };

  const deleteGroup = async (id) => {
    if (!window.confirm("Delete this group? This will remove the group record.")) return;
    try {
      const res = await fetch(process.env.REACT_APP_BACKEND_URL+`/report_groups/${id}`, { method: "DELETE" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      await fetchAll();
    } catch (err) {
      console.error("Failed to delete group:", err);
      alert("Failed to delete group.");
    }
  };

  // Quick toggle active state (optimistic)
  const toggleActiveQuick = async (group) => {
    const updatedValue = group.is_active ? 0 : 1;
    // optimistic UI
    setGroups((prev) => prev.map((g) => (g.id === group.id ? { ...g, is_active: updatedValue } : g)));
    try {
      const res = await fetch(process.env.REACT_APP_BACKEND_URL+`/report_groups/${group.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ is_active: updatedValue }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
    } catch (err) {
      console.error("toggleActiveQuick failed:", err);
      alert("Failed to toggle active state. Refreshing...");
      await fetchAll();
    }
  };

  // --- Assign / unassign members to group ---

  const isMemberInGroup = (groupId, memberId) => {
    return !!(groupMembersMap[groupId] && groupMembersMap[groupId].has(memberId));
  };

  const toggleMemberForGroup = async (groupId, memberId) => {
    const currentlyIn = isMemberInGroup(groupId, memberId);
    // optimistic update
    setGroupMembersMap((prev) => {
      const next = { ...prev };
      if (!next[groupId]) next[groupId] = new Set();
      if (currentlyIn) next[groupId].delete(memberId);
      else next[groupId].add(memberId);
      return next;
    });

    try {
      if (currentlyIn) {
        // DELETE /report_groups_members/:groupId/:memberId
        const res = await fetch(process.env.REACT_APP_BACKEND_URL+`/report_groups_members/${groupId}/${memberId}`, {
          method: "DELETE",
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
      } else {
        // POST /report_groups_members { group_id, member_id }
        const res = await fetch(process.env.REACT_APP_BACKEND_URL+`/report_groups_members`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ group_id: groupId, member_id: memberId }),
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
      }
    } catch (err) {
      console.error("toggleMemberForGroup failed:", err);
      alert("Failed to update group membership. Refreshing...");
      await fetchAll();
    }
  };

  if (loading) return <div className="p-4">Datu ielāde...</div>;
  if (error) return <div className="p-4 text-red-600">Kļūda: {error}</div>;

  return (
    <div className="p-4">
      <h2 className="text-2xl font-bold mb-4">Kontaktpersonu grupas</h2>

      {/* Add new group */}
      <div className="mb-6">
        <h3 className="font-semibold mb-2">Pievienot jaunu grupu</h3>
        <div className="flex flex-wrap gap-2 items-end">
          <input
            className="border p-2 w-64"
            placeholder="Grupas nosaukums"
            value={newGroup.name}
            onChange={(e) => setNewGroup((prev) => ({ ...prev, name: e.target.value }))}
          />
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={!!newGroup.is_active}
              onChange={(e) => setNewGroup((prev) => ({ ...prev, is_active: e.target.checked ? 1 : 0 }))}
            />
            Aktīva
          </label>
          <button onClick={addGroup} className="px-3 py-2 bg-green-600 text-white rounded">
            Pievienot
          </button>
        </div>
      </div>

      {/* Groups list */}
      <h3 className="font-semibold mb-2">Grupu saraksts</h3>
      <div className="space-y-6">
        {groups.map((group) => (
          <div key={group.id} className="border rounded p-4 shadow">
            <div className="flex items-center justify-between mb-3">
              <div>
                {editingId === group.id ? (
                  <input
                    className="border p-2"
                    value={editForm.name}
                    onChange={(e) => setEditForm((prev) => ({ ...prev, name: e.target.value }))}
                  />
                ) : (
                  <h3 className="text-lg font-semibold">{group.name}</h3>
                )}
              </div>

              <div className="flex items-center gap-2">
                {editingId === group.id ? (
                  <>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={!!editForm.is_active}
                        onChange={(e) => setEditForm((prev) => ({ ...prev, is_active: e.target.checked ? 1 : 0 }))}
                      />
                      Aktīva
                    </label>
                    <button onClick={() => saveEdit(group.id)} className="px-3 py-1 bg-green-600 text-white rounded">
                      Saglabāt
                    </button>
                    <button onClick={cancelEdit} className="px-3 py-1 bg-gray-400 text-white rounded">
                      Atcelt
                    </button>
                  </>
                ) : (
                  <>
                    <label className="flex items-center gap-2 mr-4">
                      <input type="checkbox" checked={!!group.is_active} onChange={() => toggleActiveQuick(group)} />
                      <span className="text-sm">{group.is_active ? "Aktīva" : "Neaktīva"}</span>
                    </label>
                    <button onClick={() => startEdit(group)} className="px-3 py-1 bg-blue-600 text-white rounded">
                      Labot
                    </button>
                    <button onClick={() => deleteGroup(group.id)} className="px-3 py-1 bg-red-600 text-white rounded">
                      Dzēst
                    </button>
                  </>
                )}
              </div>

            </div>

            {/* Members assignment */}
            <div>
              <div className="font-medium mb-2">Pievienotās kontaktpersonas</div>
              {members.length === 0 ? (
                <div className="text-sm text-gray-600">Nav kontaktpersonu</div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                  {members.map((m) => {
                    const checked = isMemberInGroup(group.id, m.id);
                    return (
                      <label key={m.id} className="flex items-center gap-2 border p-2 rounded hover:bg-gray-50">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleMemberForGroup(group.id, m.id)}
                        />
                        <div>
                          <div className="font-medium">{m.name} {m.surname || ""}</div>
                          <div className="text-sm text-gray-600">{m.phone_number || "—"}</div>
                        </div>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
